import { Card, Text, Button, Dialog, Portal } from 'react-native-paper'
import { View, Pressable } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import Column from '../../../../@ui/containers/grid/column'
import { createStyle } from '../../../../contexts/global-styles.provider'
import { useCurrency } from '../../payment/payment.utils'
import moment from 'moment'
interface NextMonthEstimateCardProps {
  estimate: {
    totalAmount: number
    baseAmount: number
    extraAmount: number
    tierDetails?: Array<{
      nodeId: string
      nodeName: string
      tierName: string
      basePrice: number
      seats: number
      activeUsers: number
      extraSeatPrice: number
      extraSeats: number
      totalTierCost: number
    }>
  }
}

const TierCostRow = ({
  label,
  value,
  isTotal,
}: {
  label: string
  value: number
  isTotal?: boolean
}) => {
  const currency = useCurrency(value)
  const styles = useStyle()
  return (
    <Text
      variant="bodySmall"
      style={isTotal ? styles.tierTotal : styles.tierDetail}
    >
      {label}: {currency}
    </Text>
  )
}

const ExtraSeatsRow = ({
  extraSeats,
  extraSeatPrice,
  total,
}: {
  extraSeats: number
  extraSeatPrice: number
  total: number
}) => {
  const seatPriceCurrency = useCurrency(extraSeatPrice)
  const totalCurrency = useCurrency(total)
  const { t } = useTranslation('billing')
  const styles = useStyle()
  return (
    <Text variant="bodySmall" style={styles.tierDetail}>
      {t('extra-seats')}: {extraSeats} × {seatPriceCurrency} = {totalCurrency}
    </Text>
  )
}

export const NextMonthEstimateCard = ({
  estimate,
}: NextMonthEstimateCardProps) => {
  const styles = useStyle()
  const { t } = useTranslation('billing')
  const [showDetailsDialog, setShowDetailsDialog] = useState(false)
  const totalCurrency = useCurrency(estimate.totalAmount)
  const baseCurrency = useCurrency(estimate.baseAmount)
  const extraCurrency = useCurrency(estimate.extraAmount)

  return (
    <>
      <Pressable onPress={() => setShowDetailsDialog(true)}>
        <Card style={styles.card}>
          <View style={styles.cardContent}>
            <Column gap={4}>
              <View style={styles.header}>
                <Text variant="titleMedium" style={styles.title}>
                  {t('next-month-estimate')}
                </Text>
                <Text variant="headlineSmall" style={styles.amount}>
                  {totalCurrency}
                </Text>
              </View>
              <Text variant="bodySmall" style={styles.monthText}>
                {moment().add(1, 'month').format('MMMM YYYY')}
              </Text>
              {estimate.extraAmount > 0 && (
                <Text variant="bodySmall" style={styles.extraText}>
                  {t('includes-extra-costs', { amount: extraCurrency })}
                </Text>
              )}
            </Column>
          </View>
        </Card>
      </Pressable>

      <Portal>
        <Dialog
          visible={showDetailsDialog}
          onDismiss={() => setShowDetailsDialog(false)}
        >
          <Dialog.Title>
            {t('estimated-cost-for-month', {
              month: moment().add(1, 'month').format('MMMM YYYY'),
            })}
          </Dialog.Title>
          <Dialog.Content>
            <Column gap={8}>
              <View style={styles.totalAmountRow}>
                <Text variant="labelMedium" style={styles.detailLabel}>
                  {t('base-amount')}
                </Text>
                <Text variant="bodyMedium">{baseCurrency}</Text>
              </View>
              {estimate.extraAmount > 0 && (
                <View style={styles.totalAmountRow}>
                  <Text variant="labelMedium" style={styles.detailLabel}>
                    {t('extra-amount')}
                  </Text>
                  <Text variant="bodyMedium">{extraCurrency}</Text>
                </View>
              )}
              <View style={styles.totalAmountRow}>
                <Text variant="labelMedium" style={styles.totalAmountLabel}>
                  {t('total-amount')}:
                </Text>
                <Text variant="bodyMedium" style={styles.totalAmount}>
                  {totalCurrency}
                </Text>
              </View>
              {estimate.tierDetails && estimate.tierDetails.length > 0 && (
                <View style={styles.tierDetailsSection}>
                  <Text variant="labelMedium" style={styles.detailLabel}>
                    {t('tier-breakdown')}
                  </Text>
                  {estimate.tierDetails.map((tier: any, index: number) => (
                    <View key={index} style={styles.tierItem}>
                      <Text variant="bodyMedium" style={styles.tierName}>
                        {tier.nodeName} - {tier.tierName}
                      </Text>
                      <Text variant="bodySmall" style={styles.tierDetail}>
                        {t('seats')}: {tier.seats} | {t('active-users')}:{' '}
                        {tier.activeUsers}
                      </Text>
                      <TierCostRow
                        label={t('base-price')}
                        value={tier.basePrice}
                      />
                      {tier.extraSeats > 0 && (
                        <ExtraSeatsRow
                          extraSeats={tier.extraSeats}
                          extraSeatPrice={tier.extraSeatPrice}
                          total={tier.extraSeats * tier.extraSeatPrice}
                        />
                      )}
                      <TierCostRow
                        label={t('tier-total')}
                        value={tier.totalTierCost}
                        isTotal
                      />
                    </View>
                  ))}
                </View>
              )}
            </Column>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowDetailsDialog(false)}>
              {t('close')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </>
  )
}

const useStyle = createStyle(({ theme }) => ({
  card: {
    marginVertical: theme.spacing(1),
    marginHorizontal: theme.spacing(2),
    backgroundColor: theme.colors.primaryContainer,
  },
  cardContent: {
    paddingVertical: theme.spacing(3),
    paddingHorizontal: theme.spacing(3),
  },
  header: {
    gap: theme.spacing(1),
  },
  title: {
    fontWeight: '600',
    color: theme.colors.onPrimaryContainer,
  },
  amount: {
    fontWeight: '700',
    color: theme.colors.onPrimaryContainer,
  },
  monthText: {
    color: theme.colors.onPrimaryContainer,
    opacity: 0.8,
  },
  extraText: {
    color: theme.colors.onPrimaryContainer,
    opacity: 0.9,
    fontStyle: 'italic',
  },
  detailLabel: {
    fontWeight: '600',
    marginBottom: theme.spacing(0.5),
  },
  totalAmountRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  totalAmountLabel: {
    fontWeight: '600',
  },
  totalAmount: {
    fontWeight: '600',
    fontSize: 18,
  },
  tierDetailsSection: {
    marginTop: theme.spacing(2),
    gap: theme.spacing(2),
  },
  tierItem: {
    padding: theme.spacing(2),
    backgroundColor: theme.colors.surfaceVariant,
    borderRadius: 8,
    gap: theme.spacing(0.5),
  },
  tierName: {
    fontWeight: '600',
    marginBottom: theme.spacing(0.5),
  },
  tierDetail: {
    color: theme.colors.onSurfaceVariant,
  },
  tierTotal: {
    fontWeight: '600',
    marginTop: theme.spacing(0.5),
    color: theme.colors.primary,
  },
}))
